import {
  Calendar,
  Hospital,
  PenLine,
  Phone,
  Printer,
  Send,
} from "@tamagui/lucide-icons";
import Head from "expo-router/head";
import { useCallback, useRef } from "react";
import { NativeScrollEvent, NativeSyntheticEvent } from "react-native";
import { ScrollView, XStack, YStack, useTheme } from "tamagui";

import type {
  PatientLabOrderItem,
  PatientPrescriptionItem,
} from "@/api/generated/models";
import { PatientLabOrderStatus } from "@/api/generated/models";
import { useOrders } from "@/api/queries/orders";
import Pathology from "@/assets/icons/Pathology";
import PillBottleWithPill from "@/assets/icons/PillBottleWithPill";
import { Button } from "@/components/Button";
import { Skeleton } from "@/components/Skeletons";
import { H2, Heading, Text } from "@/components/Typography";
import { brandName } from "@/config/brand";
import { getShadow } from "@/theme/helpers";
import { formatDateOnly } from "@/utils/datetime";
import { formatAddress } from "@/utils/formatting";

const LOAD_MORE_THRESHOLD = 300;

const STATUS_LABEL: Record<PatientLabOrderStatus, string> = {
  in_progress: "In progress",
  results_ready: "Results ready",
  completed: "Completed",
};

const STATUS_COLOR: Record<PatientLabOrderStatus, string> = {
  in_progress: "$fill",
  results_ready: "$yellow",
  completed: "$green",
};

function LabOrderCard({ order }: { order: PatientLabOrderItem }) {
  const theme = useTheme();
  return (
    <YStack
      alignItems="flex-start"
      backgroundColor="$fillCallout"
      borderRadius="$150"
      paddingHorizontal="$250"
      paddingVertical="$200"
      {...getShadow()}
    >
      <XStack width="100%" alignItems="flex-start" gap="$150">
        <XStack flexShrink={0}>
          <Pathology color={theme.iconBase.get()} />
        </XStack>
        <YStack flex={1} gap="$200">
          <Heading>{order.test_name}</Heading>
          <XStack alignItems="center" gap="$100" justifyContent="space-between">
            {order.ordered_at && (
              <XStack alignItems="center" gap="$100">
                <Calendar size={16} color="$iconBase" flexShrink={0} />
                <Text size="$sm" color="$textSecondary">
                  {formatDateOnly(order.ordered_at)}
                </Text>
              </XStack>
            )}

            <Text
              color={STATUS_COLOR[order.patient_status]}
              semibold
              size="$sm"
            >
              {STATUS_LABEL[order.patient_status]}
            </Text>
          </XStack>
        </YStack>
      </XStack>
    </YStack>
  );
}

const ROUTING_METHODS: Record<string, { label: string; Icon: typeof Send }> = {
  erx: { label: "Sent electronically", Icon: Send },
  phone: { label: "Called in", Icon: Phone },
  fax: { label: "Sent by fax", Icon: Printer },
  written: { label: "Written prescription", Icon: PenLine },
};

function PrescriptionCard({
  prescription,
}: {
  prescription: PatientPrescriptionItem;
}) {
  const theme = useTheme();
  const routing = prescription.routing_method
    ? ROUTING_METHODS[prescription.routing_method]
    : undefined;
  const pharmacy = prescription.pharmacy;
  const phoneContacts =
    pharmacy?.contacts?.filter(
      (contact) => contact.system === "phone" && contact.value,
    ) ?? [];
  const address = pharmacy?.address;
  const addressText = formatAddress({
    line: address?.lines,
    city: address?.city,
    state: address?.state,
    postalCode: address?.postal_code,
  });
  return (
    <YStack
      backgroundColor="$fillCallout"
      borderRadius="$150"
      paddingHorizontal="$250"
      paddingVertical="$200"
      {...getShadow()}
    >
      <XStack alignItems="flex-start" gap="$150">
        <XStack flexShrink={0}>
          <PillBottleWithPill color={theme.iconBase.get()} />
        </XStack>
        <YStack flex={1} gap="$200">
          <Heading>{prescription.rx_name || "Prescription"}</Heading>
          <XStack justifyContent="space-between" gap="$100">
            {prescription.send_date && (
              <XStack alignItems="center" gap="$100">
                <Calendar size={16} color="$iconBase" flexShrink={0} />
                <Text size="$sm" color="$textSecondary">
                  {/^\d{4}-\d{2}-\d{2}/.test(prescription.send_date)
                    ? formatDateOnly(prescription.send_date.slice(0, 10))
                    : prescription.send_date}
                </Text>
              </XStack>
            )}
            {prescription.rxFillStatus && (
              <Text size="$sm" semibold color="$textSecondary">
                {prescription.rxFillStatus === "filled"
                  ? "Filled"
                  : "Partially filled"}
              </Text>
            )}
          </XStack>
          {(pharmacy?.name || addressText || phoneContacts.length > 0) && (
            <XStack alignItems="flex-start" gap="$100">
              <Hospital size={16} color="$iconBase" flexShrink={0} />
              <YStack flex={1} gap="$100">
                {pharmacy?.name && (
                  <Text size="$sm" color="$textSecondary">
                    {pharmacy.name}
                  </Text>
                )}
                {addressText && (
                  <Text size="$sm" color="$textSecondary">
                    {addressText}
                  </Text>
                )}
                {phoneContacts.map((contact, index) => (
                  <Text key={index} size="$sm" color="$textSecondary">
                    Phone: {contact.value}
                  </Text>
                ))}
              </YStack>
            </XStack>
          )}
          {routing && (
            <XStack alignItems="center" gap="$100">
              <routing.Icon size={16} color="$iconBase" flexShrink={0} />
              <Text size="$sm" color="$textSecondary">
                {routing.label}
              </Text>
            </XStack>
          )}
        </YStack>
      </XStack>
    </YStack>
  );
}

export default function Orders() {
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isFetching,
    isLoading,
    isError,
    isFetchNextPageError,
    refetch,
  } = useOrders();
  const orders = data?.orders ?? [];

  const scrollLayoutHeight = useRef(0);
  const loadMore = useCallback(() => {
    if (hasNextPage && !isFetching && !isError) {
      void fetchNextPage({ cancelRefetch: false });
    }
  }, [hasNextPage, isFetching, isError, fetchNextPage]);

  const handleScroll = useCallback(
    (e: NativeSyntheticEvent<NativeScrollEvent>) => {
      const { contentOffset, contentSize, layoutMeasurement } = e.nativeEvent;
      const distanceFromBottom =
        contentSize.height - layoutMeasurement.height - contentOffset.y;
      if (distanceFromBottom < LOAD_MORE_THRESHOLD) {
        loadMore();
      }
    },
    [loadMore],
  );

  // When content is shorter than the viewport (no scrollbar), load more pages proactively
  const handleContentSizeChange = useCallback(
    (_: number, contentHeight: number) => {
      if (contentHeight <= scrollLayoutHeight.current) {
        loadMore();
      }
    },
    [loadMore],
  );

  return (
    <>
      <Head>
        <title>{`Orders — ${brandName}`}</title>
      </Head>
      <ScrollView
        onScroll={handleScroll}
        scrollEventThrottle={200}
        onLayout={(e) => {
          scrollLayoutHeight.current = e.nativeEvent.layout.height;
        }}
        onContentSizeChange={handleContentSizeChange}
      >
        <YStack
          padding="$screenPadding"
          gap="$300"
          maxWidth={960}
          alignSelf="center"
          width="100%"
        >
          <XStack alignItems="center">
            <H2>Orders</H2>
          </XStack>

          {isLoading && (
            <YStack gap="$200">
              <Skeleton height={72} borderRadius="$150" />
              <Skeleton height={72} borderRadius="$150" />
              <Skeleton height={72} borderRadius="$150" />
            </YStack>
          )}

          {!isLoading && !isError && orders.length === 0 && (
            <Text color="$textSecondary">No orders yet.</Text>
          )}

          {orders.length > 0 && (
            <YStack gap="$150">
              {orders.map((order) =>
                "lab" in order ? (
                  <LabOrderCard key={`lab-${order.id}`} order={order.lab} />
                ) : (
                  <PrescriptionCard
                    key={`prescription-${order.id}`}
                    prescription={order.prescription}
                  />
                ),
              )}
            </YStack>
          )}
          {isError && (
            <YStack gap="$100">
              <Text color="$textSecondary">
                {isFetchNextPageError
                  ? "Couldn’t load more orders."
                  : "Couldn’t load orders."}
              </Text>
              <Button
                dataTrackingId="orders-retry"
                disabled={isFetching}
                onPress={() =>
                  isFetchNextPageError ? fetchNextPage() : refetch()
                }
              >
                Try again
              </Button>
            </YStack>
          )}
          {hasNextPage && !isError && !isFetchingNextPage && (
            <Button
              dataTrackingId="orders-load-more"
              disabled={isFetching}
              onPress={loadMore}
            >
              Load more
            </Button>
          )}
          {isFetchingNextPage && <Skeleton height={72} borderRadius="$150" />}
        </YStack>
      </ScrollView>
    </>
  );
}
